// RosterBattles.js
// Mirrors RosterBattlesView.swift.
// Two slash-panel mode cards (Pick-Up Draft + Knockout) plus, below them, an ACTIVE DRAFTS
// section that lists the user's in-progress and just-finished Pick Up games — exactly like the
// iOS landing screen. The Pick Up card's sport glyphs light up for sports with a live game and
// show a teal dot for sports with an incoming challenge.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import { listenToIncomingChallenges, isUserTurn } from "../managers/pickupService";
import { listenToActivePickUpGames } from "../managers/matchmakingService";
import { loadAliveSports } from "../managers/knockoutService";
import ActiveGameRow from "../components/ActiveGameRow";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import strategyIcon from "../assets/strategy-fill.svg";
import treeIcon from "../assets/tree-structure.svg";

const APP_THEME = "#d4f53c";
const TEAL = "#00BFB8";

const SPORTS = [
  { id: "football", hex: "#00b04f", icon: footballIcon },
  { id: "baseball", hex: "#db2626", icon: baseballIcon },
  { id: "basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey", hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer", hex: "#1a66e6", icon: soccerIcon },
];

const isLive = (g) => g.status === "waiting" || g.status === "active";
const isDone = (g) => g.status === "complete" || g.status === "forfeited";

function isKnockoutDay() {
  const wd = new Date().toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", weekday: "short" });
  return wd === "Sat";
}

export default function RosterBattles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid || null;

  const [hasAppeared, setHasAppeared] = useState(false);
  const [games, setGames] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [aliveKnockoutSports, setAliveKnockoutSports] = useState(new Set());

  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!uid) return undefined;
    // Real-time: ACTIVE DRAFTS updates the instant a pick lands (no more 30s poll).
    const unsubChallenges = listenToIncomingChallenges(uid, setIncoming);
    const unsubGames = listenToActivePickUpGames(uid, setGames);
    // Knockout alive-sports: a one-time read (standings change at most once a day).
    loadAliveSports(uid).then(setAliveKnockoutSports).catch(() => {});
    return () => {
      unsubChallenges();
      unsubGames();
    };
  }, [uid]);

  const activeSports = new Set(games.filter(isLive).map((g) => g.sport));
  const alertSports = new Set(incoming.map((c) => c.sport));

  // Active drafts ordering: completed-unviewed → your-turn → waiting.
  const completedUnviewed = games.filter(isDone).sort((a, b) => b.lastActionAt.getTime() - a.lastActionAt.getTime());
  const yourTurn = games
    .filter((g) => isLive(g) && isUserTurn(g, uid))
    .sort((a, b) => a.turnExpiresAt.getTime() - b.turnExpiresAt.getTime());
  const waiting = games
    .filter((g) => isLive(g) && !isUserTurn(g, uid))
    .sort((a, b) => a.turnExpiresAt.getTime() - b.turnExpiresAt.getTime());
  const orderedGames = [...completedUnviewed, ...yourTurn, ...waiting];

  const openGame = (g) =>
    navigate(isDone(g) ? `/battles/pickup/complete?gameID=${g.id}` : `/battles/pickup/draft?gameID=${g.id}`);

  return (
    <div style={styles.container}>
      <div style={{ paddingTop: "12px" }} />

      <SectionRule label="ROSTER BATTLES" />

      <div style={styles.cards}>
        <BattleModeCard
          icon={strategyIcon}
          title="PICK-UP DRAFT"
          subtitle="HEAD TO HEAD"
          accent={TEAL}
          delay={0.08}
          appeared={hasAppeared}
          activeSports={activeSports}
          alertSports={alertSports}
          onClick={() => navigate("/battles/pickup")}
        />
        <BattleModeCard
          icon={treeIcon}
          iconRotation={180}
          title="KNOCKOUT MODE"
          subtitle="SURVIVAL LEAGUES"
          accent="#A68CF2"
          tagline={isKnockoutDay() ? "POOLS START TODAY" : null}
          taglineColor="#B899FF"
          delay={0.15}
          appeared={hasAppeared}
          activeSports={aliveKnockoutSports}
          onClick={() => navigate("/battles/knockout")}
        />
      </div>

      {orderedGames.length > 0 && (
        <>
          <SectionRule label="ACTIVE DRAFTS" />
          <div style={styles.gameList}>
            {orderedGames.map((g) => (
              <ActiveGameRow key={g.id} game={g} currentUserID={uid} isCompleted={isDone(g)} onTap={() => openGame(g)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionRule({ label, color = APP_THEME }) {
  return (
    <div style={styles.sectionRule}>
      <div style={{ ...styles.rule, background: `linear-gradient(to left, ${color}8c, transparent)` }} />
      <span style={{ ...styles.sectionLabel, color }}>{label}</span>
      <div style={{ ...styles.rule, background: `linear-gradient(to right, ${color}8c, transparent)` }} />
    </div>
  );
}

function BattleModeCard({ icon, iconRotation = 0, title, subtitle, accent, tagline, taglineColor, delay, appeared, activeSports, alertSports, onClick }) {
  const [pressed, setPressed] = useState(false);
  const dimGlyphs = activeSports instanceof Set; // only the Pick Up card passes a set

  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        ...styles.card,
        border: `1.5px solid ${accent}59`,
        boxShadow: `0 4px 7px ${accent}24, 0 2px 4px rgba(0,0,0,0.25)`,
        opacity: appeared ? 1 : 0,
        transform: `${appeared ? "translateY(0)" : "translateY(24px)"} scale(${pressed ? 0.97 : 1})`,
        transition: `opacity 0.5s ease ${delay}s, transform 0.5s cubic-bezier(0.2,0.8,0.3,1) ${delay}s`,
      }}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <polygon points="0,0 26,0 15,100 0,100" fill={accent} />
        <line x1="26" y1="0" x2="15" y2="100" stroke="rgba(255,255,255,0.22)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      </svg>

      <img
        src={icon}
        alt=""
        style={{ position: "absolute", left: "1.5%", top: "50%", width: 58, height: 58, filter: "brightness(0)", opacity: 0.85, transform: `translateY(-50%) rotate(${iconRotation}deg)` }}
      />

      <div style={styles.rightContent}>
        <span style={styles.cardTitle}>{title}</span>
        <span style={styles.cardSubtitle}>{subtitle}</span>
        {tagline && (
          <span style={{ ...styles.cardTagline, color: taglineColor || accent, textShadow: `0 0 6px ${(taglineColor || accent)}b3` }}>
            {tagline}
          </span>
        )}
        <div style={styles.sportRow}>
          {SPORTS.map((s) => {
            const active = !dimGlyphs || activeSports.has(s.id);
            const color = active ? s.hex : "rgba(255,255,255,0.18)";
            const alert = alertSports instanceof Set && alertSports.has(s.id);
            return (
              <span key={s.id} style={{ position: "relative", display: "inline-block", width: 24, height: 24 }}>
                <span
                  style={{
                    display: "block",
                    width: 24,
                    height: 24,
                    background: color,
                    WebkitMaskImage: `url(${s.icon})`,
                    maskImage: `url(${s.icon})`,
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                  }}
                />
                {alert && <span style={{ position: "absolute", top: -2, right: -2, width: 7, height: 7, borderRadius: 4, background: TEAL }} />}
              </span>
            );
          })}
        </div>
      </div>
    </button>
  );
}

const styles = {
  container: { width: "100%", maxWidth: "480px", margin: "0 auto", padding: "0 16px 32px", display: "flex", flexDirection: "column", gap: "14px" },
  sectionRule: { display: "flex", alignItems: "center", gap: "10px" },
  rule: { flex: 1, height: "1px" },
  sectionLabel: { fontSize: "12px", fontWeight: "800", letterSpacing: "2.5px", whiteSpace: "nowrap" },
  cards: { display: "flex", flexDirection: "column", gap: "14px" },
  gameList: { display: "flex", flexDirection: "column", gap: "10px" },
  card: { position: "relative", width: "100%", height: "143px", borderRadius: "14px", overflow: "hidden", background: "#12161F", cursor: "pointer", padding: 0 },
  rightContent: { position: "absolute", left: "29%", right: "8px", top: "50%", transform: "translateY(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" },
  cardTitle: { fontSize: "22px", fontWeight: "900", fontStyle: "italic", color: "rgba(255,255,255,0.88)", letterSpacing: "3px", textAlign: "center", lineHeight: 1.05 },
  cardSubtitle: { fontSize: "14px", fontWeight: "500", fontStyle: "italic", color: "rgba(255,255,255,0.45)", textAlign: "center" },
  cardTagline: { fontSize: "11px", fontWeight: "700", letterSpacing: "1.5px", marginTop: "2px" },
  sportRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px", width: "100%", maxWidth: "230px", marginTop: "4px" },
};
